import type {
  SupabaseClient,
} from '@supabase/supabase-js';

import type {
  GraphMember,
} from '@/contexts/FamilyGraphContext';

import {
  loadCanonicalTreeRelationshipHistoryExplicit,
  loadCanonicalTreeSpousePartnerEdgesExplicit,
} from './CanonicalTreeExplicitExternalEvidenceLoaders';

import {
  canonicalTreeExternalEvidenceLoadResultsAreSnapshotReady,
  type CanonicalTreeRelationshipHistoryLoadResult,
  type CanonicalTreeSpousePartnerEdgeLoadResult,
} from './CanonicalTreeExternalEvidenceLoaderResult';

import {
  beginCanonicalTreeExternalEvidenceSnapshotLoad,
  completeCanonicalTreeExternalEvidenceSnapshotLoad,
  failCanonicalTreeExternalEvidenceSnapshotLoad,
  type CanonicalTreeExternalEvidenceRefreshReason,
  type CanonicalTreeExternalEvidenceSnapshot,
} from './CanonicalTreeExternalEvidenceSnapshot';

export interface CanonicalTreeExternalEvidenceSnapshotLoaderInput {
  readonly supabase:
    SupabaseClient;

  readonly ownerUserId:
    string;

  readonly members:
    readonly GraphMember[];

  readonly refreshReason:
    CanonicalTreeExternalEvidenceRefreshReason;

  readonly now?:
    () => string;
}

export interface CanonicalTreeExternalEvidenceSnapshotLoaderResult {
  readonly loadingSnapshot:
    CanonicalTreeExternalEvidenceSnapshot;

  readonly snapshot:
    CanonicalTreeExternalEvidenceSnapshot;

  readonly spousePartnerEdges:
    CanonicalTreeSpousePartnerEdgeLoadResult;

  readonly relationshipHistory:
    CanonicalTreeRelationshipHistoryLoadResult;

  readonly snapshotReady:
    boolean;
}

const combineFailures = (
  input: {
    readonly spousePartnerEdges:
      CanonicalTreeSpousePartnerEdgeLoadResult;

    readonly relationshipHistory:
      CanonicalTreeRelationshipHistoryLoadResult;
  },
): string => {
  const failures:
    string[] = [];

  if (
    input.spousePartnerEdges
      .failure
  ) {
    const failure =
      input.spousePartnerEdges
        .failure;

    failures.push(
      [
        'spouse_partner_edges',
        input.spousePartnerEdges
          .status,
        failure.code ??
          'NO-CODE',
        failure.message,
        failure.details,
      ]
        .filter(
          value =>
            value !==
            null,
        )
        .join(':'),
    );
  }

  if (
    input.relationshipHistory
      .failure
  ) {
    const failure =
      input.relationshipHistory
        .failure;

    failures.push(
      [
        'relationship_history',
        input.relationshipHistory
          .status,
        failure.code ??
          'NO-CODE',
        failure.message,
        failure.details,
      ]
        .filter(
          value =>
            value !==
            null,
        )
        .join(':'),
    );
  }

  if (
    failures.length ===
    0
  ) {
    failures.push(
      'External evidence load results were not snapshot-ready.',
    );
  }

  return failures.join(
    ' | ',
  );
};

export const loadCanonicalTreeExternalEvidenceSnapshot = async (
  input:
    CanonicalTreeExternalEvidenceSnapshotLoaderInput,
): Promise<CanonicalTreeExternalEvidenceSnapshotLoaderResult> => {
  const now =
    input.now ??
    (() =>
      new Date()
        .toISOString());

  const requestedAt =
    now();

  const loadingSnapshot =
    beginCanonicalTreeExternalEvidenceSnapshotLoad({
      ownerUserId:
        input.ownerUserId,

      members:
        input.members,

      requestedAt,

      refreshReason:
        input.refreshReason,
    });

  const [
    spousePartnerEdges,
    relationshipHistory,
  ] =
    await Promise.all([
      loadCanonicalTreeSpousePartnerEdgesExplicit({
        supabase:
          input.supabase,

        ownerUserId:
          input.ownerUserId,

        now,
      }),

      loadCanonicalTreeRelationshipHistoryExplicit({
        supabase:
          input.supabase,

        ownerUserId:
          input.ownerUserId,

        now,
      }),
    ]);

  const snapshotReady =
    canonicalTreeExternalEvidenceLoadResultsAreSnapshotReady({
      spousePartnerEdges,

      relationshipHistory,
    });

  const completedAt =
    now();

  const snapshot =
    snapshotReady
      ? completeCanonicalTreeExternalEvidenceSnapshotLoad({
          ownerUserId:
            input.ownerUserId,

          members:
            input.members,

          spousePartnerEdges:
            spousePartnerEdges
              .rows,

          relationshipHistory:
            relationshipHistory
              .rows,

          requestedAt,

          completedAt,

          refreshReason:
            input.refreshReason,
        })
      : failCanonicalTreeExternalEvidenceSnapshotLoad({
          ownerUserId:
            input.ownerUserId,

          members:
            input.members,

          requestedAt,

          completedAt,

          refreshReason:
            input.refreshReason,

          error:
            combineFailures({
              spousePartnerEdges,

              relationshipHistory,
            }),
        });

  return Object.freeze({
    loadingSnapshot,

    snapshot,

    spousePartnerEdges,

    relationshipHistory,

    snapshotReady:
      snapshotReady &&
      snapshot.status ===
        'LOADED' &&
      snapshot.readyForGateway,
  });
};
